/**
 * Value formatting for a support console — timestamps, and the counts on the
 * overview.
 *
 * Everything the API hands us is an ISO-8601 string, straight out of a Postgres
 * `timestamptz` (see `shared/internal_contract.ts`). This module is the only
 * place in the SPA that parses one — nothing else should be calling
 * `new Date()` on a field off the wire.
 *
 * Two renderings: `stamp()` for the unambiguous absolute value that goes in a
 * table cell, and `relative()` for the "is this recent?" read an operator
 * actually makes when they scan a seat list. Rows use both — relative in the
 * cell, absolute in the `title` — because "3d ago" answers the question and
 * `2026-08-09 14:02Z` settles the argument.
 *
 * An unparseable or absent value renders as an em dash rather than throwing or
 * printing `Invalid Date`. A support console that blanks a cell is annoying; one
 * that white-screens on a malformed row is unusable.
 */

/**
 * Formatted by hand rather than with `Intl.RelativeTimeFormat`: its narrow
 * style differs between ICU versions ("3 days ago" vs "3d ago"), which makes
 * both the column width and the tests unstable. These are codes in a dense
 * table, not prose.
 */
const RELATIVE_UNITS: ReadonlyArray<readonly [suffix: string, ms: number]> = [
  ["y", 365 * 24 * 60 * 60 * 1000],
  ["mo", 30 * 24 * 60 * 60 * 1000],
  ["d", 24 * 60 * 60 * 1000],
  ["h", 60 * 60 * 1000],
  ["m", 60 * 1000],
];

const DASH = "—";

const pad = (n: number) => String(n).padStart(2, "0");

/** NaN for null, undefined, or anything `Date` cannot read. */
function msFrom(iso: string | null | undefined): number {
  if (!iso) return Number.NaN;
  return new Date(iso).getTime();
}

/** `2026-08-12` — UTC. For columns where the time of day is noise. */
export function isoDate(iso: string | null | undefined): string {
  const ms = msFrom(iso);
  if (Number.isNaN(ms)) return DASH;
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** `2026-08-12 14:02Z` — UTC, because two admins in two timezones read this. */
export function stamp(iso: string | null | undefined): string {
  const ms = msFrom(iso);
  if (Number.isNaN(ms)) return DASH;
  const d = new Date(ms);
  return `${isoDate(iso)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}Z`;
}

/** `3d ago`, `in 2h`, `now`. */
export function relative(iso: string | null | undefined, nowMs: number = Date.now()): string {
  const ms = msFrom(iso);
  if (!Number.isFinite(ms)) return DASH;
  const delta = ms - nowMs;
  const abs = Math.abs(delta);
  if (abs < 60_000) return "now";
  for (const [suffix, size] of RELATIVE_UNITS) {
    if (abs < size) continue;
    const magnitude = Math.floor(abs / size);
    return delta < 0 ? `${magnitude}${suffix} ago` : `in ${magnitude}${suffix}`;
  }
  return "now";
}

/**
 * `1,284`. Grouped, because the overview's tiles are read at a glance and
 * `12840` and `1284` are the same shape from two feet away.
 *
 * Pinned to en-US rather than the browser's locale, for the same reason
 * `stamp()` is pinned to UTC: two operators read the same figure off the same
 * screenshot, and `1.284` meaning one thousand to one of them and one-point-two
 * to the other is a support call about a support tool.
 *
 * An absent or non-finite value is an em dash — a missing count must not render
 * as `NaN`, and it must not render as `0`, which is a real and different answer.
 */
export function count(n: number | null | undefined): string {
  if (typeof n !== "number" || !Number.isFinite(n)) return DASH;
  return n.toLocaleString("en-US");
}

/** Uuids and hashes are longer than anybody reads in a table cell. */
export function shortHash(hash: string, chars = 12): string {
  return hash.length <= chars ? hash : `${hash.slice(0, chars)}…`;
}
